// public/js/features/distribution/requests-modal.js
// Superadmin per-tenant device-request manager (docs/device-distribution.md).
// Lists a tenant's requests, lets you create a basket request (several models at
// once), and complete an open request with a delivery record (date, carrier,
// tracking number). On-hand inventory is NOT edited here — only planned requests.
import { listTenantRequests, createRequest, updateRequest } from './api.js';
import { formatPersonName } from '../../utils/person-name.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return iso; } };
const todayYmd = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const CARRIERS = ['DHL', 'hand-delivered'];

// tenant: { tenantId, name }; models: string[] of known model names; onChanged: () => void
export async function openRequestsModal(tenant, models, onChanged) {
  const id = `req-modal-${Math.abs(hashCode(tenant.tenantId))}`;
  document.getElementById(id)?.remove();

  const modelOptions = models.map(m => `<option value="${esc(m)}">${esc(m)}</option>`).join('');

  const html = `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-clipboard-list me-2"></i>Requests — ${esc(tenant.name)}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="card mb-3">
              <div class="card-header py-2 small fw-semibold"><i class="fa-solid fa-plus me-1"></i>New request</div>
              <div class="card-body">
                <div id="req-basket" class="mb-2"></div>
                <div class="row g-2 align-items-end">
                  <div class="col-sm-6">
                    <label class="form-label small mb-1">Model</label>
                    <select class="form-select form-select-sm js-req-model">${modelOptions}</select>
                  </div>
                  <div class="col-sm-3">
                    <label class="form-label small mb-1">Qty</label>
                    <input type="number" min="1" value="1" class="form-control form-control-sm js-req-qty">
                  </div>
                  <div class="col-sm-3">
                    <button class="btn btn-sm btn-outline-primary w-100 js-req-add"><i class="fa-solid fa-plus me-1"></i>Add</button>
                  </div>
                </div>
                <hr class="my-3">
                <div class="row g-2 align-items-end">
                  <div class="col-sm-4">
                    <label class="form-label small mb-1">Priority <span class="text-muted">(1 low – 10 urgent)</span>: <span class="js-req-prio-val fw-semibold">5</span></label>
                    <input type="range" min="1" max="10" value="5" class="form-range js-req-prio">
                  </div>
                  <div class="col-sm-3">
                    <label class="form-label small mb-1">Desired delivery</label>
                    <input type="date" class="form-control form-control-sm js-req-date" min="${todayYmd()}">
                  </div>
                  <div class="col-sm-5">
                    <label class="form-label small mb-1">Note (optional)</label>
                    <input type="text" class="form-control form-control-sm js-req-note" placeholder="e.g. for new Miami hub">
                  </div>
                </div>
                <div class="text-end mt-3">
                  <button class="btn btn-primary btn-sm js-req-create" disabled><i class="fa-solid fa-paper-plane me-1"></i>Create request</button>
                </div>
              </div>
            </div>
            <div class="small fw-semibold text-secondary mb-2">Existing requests</div>
            <div id="req-list"><div class="text-muted small py-2"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', html);
  const el = document.getElementById(id);
  const modal = new bootstrap.Modal(el);
  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });

  let dirty = false;   // did anything change (so the page refreshes on close)?
  const basket = [];   // [{ model, qty }]

  const basketEl = el.querySelector('#req-basket');
  const createBtn = el.querySelector('.js-req-create');
  const renderBasket = () => {
    if (!basket.length) {
      basketEl.innerHTML = `<div class="text-muted small fst-italic">No items yet — add a model and quantity.</div>`;
      createBtn.disabled = true;
      return;
    }
    basketEl.innerHTML = basket.map((it, i) => `
      <span class="badge rounded-pill text-bg-light border me-1 mb-1" style="font-size:0.85em;">
        <span class="fw-semibold">${it.qty}×</span> ${esc(it.model)}
        <a href="#" class="text-danger ms-1 js-req-rm" data-i="${i}" title="Remove"><i class="fa-solid fa-xmark"></i></a>
      </span>`).join('');
    createBtn.disabled = false;
    basketEl.querySelectorAll('.js-req-rm').forEach(a => a.addEventListener('click', (ev) => {
      ev.preventDefault(); basket.splice(+a.dataset.i, 1); renderBasket();
    }));
  };
  renderBasket();

  el.querySelector('.js-req-add').addEventListener('click', () => {
    const model = el.querySelector('.js-req-model').value;
    const qty = Math.max(1, Math.round(+el.querySelector('.js-req-qty').value || 1));
    if (!model) return;
    const existing = basket.find(b => b.model === model);
    if (existing) existing.qty += qty; else basket.push({ model, qty });
    el.querySelector('.js-req-qty').value = '1';
    renderBasket();
  });

  const prio = el.querySelector('.js-req-prio');
  prio.addEventListener('input', () => { el.querySelector('.js-req-prio-val').textContent = prio.value; });

  createBtn.addEventListener('click', async () => {
    if (!basket.length) return;
    createBtn.disabled = true;
    try {
      await createRequest({
        tenantId: tenant.tenantId,
        items: basket.map(b => ({ model: b.model, qty: b.qty })),
        priority: +prio.value,
        targetDate: el.querySelector('.js-req-date').value || null,
        note: el.querySelector('.js-req-note').value.trim() || null,
      });
      basket.length = 0; renderBasket();
      el.querySelector('.js-req-note').value = '';
      el.querySelector('.js-req-date').value = '';
      prio.value = '5'; el.querySelector('.js-req-prio-val').textContent = '5';
      dirty = true;
      await refreshList();
    } catch (e) {
      alert('Failed to create request.');
      createBtn.disabled = false;
    }
  });

  const listEl = el.querySelector('#req-list');
  async function refreshList() {
    let requests;
    try { requests = await listTenantRequests(tenant.tenantId); }
    catch { listEl.innerHTML = `<div class="text-danger small">Failed to load requests.</div>`; return; }
    if (!requests.length) { listEl.innerHTML = `<div class="text-muted small fst-italic">No requests yet.</div>`; return; }
    // Open first (by priority desc, then oldest), then completed (most recent first).
    const open = requests.filter(r => r.status === 'open')
      .sort((a, b) => (b.priority - a.priority) || (a.requestedAt || '').localeCompare(b.requestedAt || ''));
    const done = requests.filter(r => r.status !== 'open')
      .sort((a, b) => (b.completion?.date || '').localeCompare(a.completion?.date || ''));
    listEl.innerHTML = [...open, ...done].map(cardFor).join('');
    wireCards();
  }

  function cardFor(r) {
    const items = (r.items || []).map(it => `<span class="badge text-bg-light border me-1"><span class="fw-semibold">${it.qty}×</span> ${esc(it.model)}</span>`).join('');
    const isOpen = r.status === 'open';
    const prioBadge = `<span class="badge ${r.priority >= 8 ? 'text-bg-danger' : r.priority >= 4 ? 'text-bg-warning' : 'text-bg-secondary'}">P${r.priority}</span>`;
    // Open requests: priority is editable at any time (a compact inline select).
    const prioControl = isOpen
      ? `<select class="js-prio form-select form-select-sm d-inline-block align-middle" title="Priority (9 = highest)"
                 style="width:auto;padding:.1rem 1.5rem .1rem .5rem;font-size:.72rem;">
           ${[9, 8, 7, 6, 5, 4, 3, 2, 1].map(p => `<option value="${p}"${p === r.priority ? ' selected' : ''}>P${p}</option>`).join('')}
         </select>`
      : prioBadge;
    const completed = r.completion
      ? `<div class="small text-success mt-1"><i class="fa-solid fa-check me-1"></i>Delivered ${fmtDate(r.completion.date)}${r.completion.carrier ? ` · ${esc(r.completion.carrier)}` : ''}${r.completion.trackingNumber ? ` · <span class="font-monospace">${esc(r.completion.trackingNumber)}</span>` : ''}</div>`
      : '';
    return `
      <div class="border rounded p-2 mb-2 ${isOpen ? '' : 'bg-light'}" data-rid="${esc(r.requestId)}">
        <div class="d-flex justify-content-between align-items-start">
          <div style="min-width:0;flex:1;">
            <div class="mb-1 d-flex align-items-center flex-wrap gap-1">${prioControl} ${items}</div>
            <div class="text-muted small">Requested ${fmtDate(r.requestedAt)}${r.createdBy ? ` · by <span class="fw-semibold" title="${esc(r.createdBy)}">${esc(formatPersonName(r.createdBy))}</span>` : ''}</div>
            ${isOpen
              ? `<div class="small mt-1 d-flex align-items-center gap-2">
                   <span class="text-muted"><i class="fa-solid fa-truck-fast me-1"></i>Wanted by</span>
                   <input type="date" class="form-control form-control-sm js-date-input" style="width:auto;" value="${r.targetDate ? esc(String(r.targetDate).slice(0, 10)) : ''}" min="${todayYmd()}">
                 </div>`
              : (r.targetDate ? `<div class="small mt-1 text-muted"><i class="fa-solid fa-truck-fast me-1"></i>Wanted by ${fmtDate(r.targetDate)}</div>` : '')}
            <div class="small mt-1 js-note-block">
              <span class="js-note-view">
                <i class="fa-solid fa-note-sticky text-secondary me-1"></i>${r.note ? esc(r.note) : '<span class="text-muted fst-italic">no note</span>'}
                ${isOpen ? '<a href="#" class="js-note-edit ms-2 text-decoration-none" title="Edit note"><i class="fa-solid fa-pen"></i></a>' : ''}
              </span>
              <div class="input-group input-group-sm mt-1 js-note-editor d-none" style="max-width:420px;">
                <input type="text" class="form-control js-note-input" maxlength="2000" value="${r.note ? esc(r.note) : ''}" placeholder="Add a note…">
                <button class="btn btn-success js-note-save" type="button" title="Save"><i class="fa-solid fa-check"></i></button>
                <button class="btn btn-outline-secondary js-note-cancel" type="button" title="Cancel"><i class="fa-solid fa-xmark"></i></button>
              </div>
            </div>
            ${completed}
          </div>
          <div class="text-nowrap ms-2">
            ${isOpen
              ? `<button class="btn btn-sm btn-outline-success js-req-complete" title="Mark delivered"><i class="fa-solid fa-truck me-1"></i>Complete</button>
                 <button class="btn btn-sm btn-outline-danger js-req-cancel" title="Cancel request"><i class="fa-solid fa-xmark"></i></button>`
              : `<span class="badge text-bg-success">Completed</span>`}
          </div>
        </div>
        <div class="js-req-complete-form mt-2" hidden>
          <div class="row g-2 align-items-end">
            <div class="col-sm-4">
              <label class="form-label small mb-1">Delivered on</label>
              <input type="date" class="form-control form-control-sm js-cf-date" value="${todayYmd()}" max="${todayYmd()}">
            </div>
            <div class="col-sm-4">
              <label class="form-label small mb-1">Carrier</label>
              <select class="form-select form-select-sm js-cf-carrier">${CARRIERS.map(c => `<option value="${c}">${c}</option>`).join('')}</select>
            </div>
            <div class="col-sm-4 js-cf-tracking-wrap">
              <label class="form-label small mb-1">Tracking #</label>
              <input type="text" class="form-control form-control-sm js-cf-tracking" placeholder="optional">
            </div>
          </div>
          <div class="text-end mt-2">
            <button class="btn btn-sm btn-secondary js-cf-back">Back</button>
            <button class="btn btn-sm btn-success js-cf-save"><i class="fa-solid fa-check me-1"></i>Save delivery</button>
          </div>
        </div>
      </div>`;
  }

  function wireCards() {
    listEl.querySelectorAll('[data-rid]').forEach(card => {
      const rid = card.dataset.rid;
      const form = card.querySelector('.js-req-complete-form');
      card.querySelector('.js-req-complete')?.addEventListener('click', () => { form.hidden = false; });
      card.querySelector('.js-cf-back')?.addEventListener('click', () => { form.hidden = true; });
      // Hide tracking # for hand-delivered.
      const carrierSel = card.querySelector('.js-cf-carrier');
      const trackWrap = card.querySelector('.js-cf-tracking-wrap');
      carrierSel?.addEventListener('change', () => { trackWrap.style.visibility = carrierSel.value === 'hand-delivered' ? 'hidden' : 'visible'; });
      card.querySelector('.js-cf-save')?.addEventListener('click', async () => {
        const saveBtn = card.querySelector('.js-cf-save');
        saveBtn.disabled = true;
        const carrier = carrierSel.value;
        try {
          await updateRequest(rid, {
            tenantId: tenant.tenantId,
            status: 'completed',
            completion: {
              date: new Date(`${card.querySelector('.js-cf-date').value || todayYmd()}T12:00:00`).toISOString(),
              carrier,
              trackingNumber: carrier === 'hand-delivered' ? null : (card.querySelector('.js-cf-tracking').value.trim() || null),
            },
          });
          dirty = true;
          await refreshList();
        } catch { alert('Failed to save delivery.'); saveBtn.disabled = false; }
      });
      card.querySelector('.js-req-cancel')?.addEventListener('click', async () => {
        if (!confirm('Cancel this request? It will be removed from open requests.')) return;
        try { await updateRequest(rid, { tenantId: tenant.tenantId, status: 'completed', completion: null }); dirty = true; await refreshList(); }
        catch { alert('Failed to cancel request.'); }
      });

      // Adjust priority at any time (open requests).
      card.querySelector('.js-prio')?.addEventListener('change', async (ev) => {
        const sel = ev.target; sel.disabled = true;
        try { await updateRequest(rid, { tenantId: tenant.tenantId, priority: Number(sel.value) }); dirty = true; await refreshList(); }
        catch { alert('Failed to update priority.'); sel.disabled = false; }
      });

      // Adjust the desired delivery date at any time (open requests).
      card.querySelector('.js-date-input')?.addEventListener('change', async (ev) => {
        const inp = ev.target; inp.disabled = true;
        try { await updateRequest(rid, { tenantId: tenant.tenantId, targetDate: inp.value || null }); dirty = true; await refreshList(); }
        catch { alert('Failed to update target date.'); inp.disabled = false; }
      });

      // Inline note editing (open requests only).
      const noteView = card.querySelector('.js-note-view');
      const noteEditor = card.querySelector('.js-note-editor');
      const noteInput = card.querySelector('.js-note-input');
      card.querySelector('.js-note-edit')?.addEventListener('click', (ev) => {
        ev.preventDefault();
        noteView.classList.add('d-none'); noteEditor.classList.remove('d-none');
        noteInput.focus();
      });
      card.querySelector('.js-note-cancel')?.addEventListener('click', () => {
        noteEditor.classList.add('d-none'); noteView.classList.remove('d-none');
      });
      card.querySelector('.js-note-save')?.addEventListener('click', async () => {
        const saveBtn = card.querySelector('.js-note-save');
        saveBtn.disabled = true;
        try {
          await updateRequest(rid, { tenantId: tenant.tenantId, note: noteInput.value.trim() || null });
          dirty = true;
          await refreshList();
        } catch { alert('Failed to save note.'); saveBtn.disabled = false; }
      });
      noteInput?.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') card.querySelector('.js-note-save')?.click(); });
    });
  }

  el.addEventListener('hidden.bs.modal', () => { if (dirty) onChanged?.(); }, { once: true });
  modal.show();
  await refreshList();
}

function hashCode(s) { let h = 0; for (let i = 0; i < String(s).length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; } return h; }
